import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, SearchIcon } from 'lucide-react';
import { SectionHeading } from './ui/SectionHeading';
import { previewPages } from '../data/previews';
export function LookInsideCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const total = previewPages.length;
  const goTo = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = (index + total) % total;
    const slide = track.children[clamped] as HTMLElement | undefined;
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({
      left: slide.offsetLeft - track.offsetLeft - (track.clientWidth - slide.clientWidth) / 2,
      behavior: reduce ? 'auto' : 'smooth'
    });
  }, [total]);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const center = track.scrollLeft + track.clientWidth / 2;
      let closest = 0;
      let distance = Infinity;
      Array.from(track.children).forEach((child, index) => {
        const el = child as HTMLElement;
        const childCenter = el.offsetLeft - track.offsetLeft + el.clientWidth / 2;
        const d = Math.abs(childCenter - center);
        if (d < distance) {
          distance = d;
          closest = index;
        }
      });
      setActive(closest);
    };
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', onScroll);
  }, []);
  const arrowClasses = 'absolute top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-electric bg-industrial text-silver shadow-glow transition-colors duration-150 ease-out hover:bg-navy focus:outline-none focus-visible:ring-4 focus-visible:ring-electric/50';
  return <section className="border-t border-electric/30 bg-navy py-16 sm:py-20" aria-labelledby="inside-title" data-id="element-94" data-mp="3qnt9a5t12ltb">
      <div className="mx-auto max-w-6xl px-4" data-id="element-95" data-mp="1oyx618f7i11e">
        <div className="text-center" data-id="element-96" data-mp="1tsi6d749v73r">
          <p className="inline-flex items-center gap-2 font-display text-base font-extrabold uppercase tracking-[.18em] text-gold" data-id="element-97" data-mp="1ttji7dt43xmj">
            <SearchIcon className="h-4 w-4" aria-hidden="true" data-id="element-98" data-mp="1k1dq842v1g7c" /> Look Inside
          </p>
          <SectionHeading id="inside-title" className="mt-3" data-id="element-99" data-mp="27r82ajfcv0qj">
            Discover What&apos;s Inside <span className="text-gold" data-id="element-100" data-mp="27pes4u5j9yds">MECHANICAL ENGINEERING — PROFESSIONAL EDITION™</span>
          </SectionHeading>
          <p className="mx-auto mt-6 max-w-3xl leading-relaxed text-steel" data-id="element-101" data-mp="1yqtdha36n3ia">
            Engineering fundamentals, applied mechanics, forces and motion, engineering materials, stress and strain,
            mechanical strength, machine elements, gears, bearings, shafts, mechanical design, power transmission,
            thermodynamics, fluid mechanics, heat transfer, pumps, compressors, turbines, CNC machining, manufacturing
            processes, industrial machinery, equipment reliability, preventive maintenance, mechanical troubleshooting,
            engineering formulas, materials selection, and essential mechanical engineering terminology.
          </p>
        </div>

        <div className="relative mt-10" role="region" aria-roledescription="carousel" aria-label="Sample pages from the professional edition" data-id="element-102" data-mp="osjz1v9xsqal">
          <div ref={trackRef} className="flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-[10%] pb-4 pt-2 [-ms-overflow-style:none] [scrollbar-width:none] sm:gap-5 md:px-[4%] [&::-webkit-scrollbar]:hidden" data-id="element-103" data-mp="140plcf5i7gx">
            {previewPages.map((page, index) => <figure key={page.src} className={`w-[80%] shrink-0 snap-center overflow-hidden rounded-2xl border-2 bg-industrial transition-[border-color,opacity,box-shadow] duration-200 ease-out sm:w-[60%] md:w-[30%] ${index === active ? 'border-electric opacity-100 shadow-glow-strong' : 'border-electric/25 opacity-70'}`} aria-roledescription="slide" aria-label={`${index + 1} of ${total}`} data-id="element-104" data-mp="6229u0obcctz" data-mpi={index}>
                <img src={page.src} alt={page.alt} loading="lazy" className="aspect-[4/5] w-full object-cover" data-id="element-105" data-mp="2gsz7iv2p6lq7" />
              </figure>)}
          </div>

          <button type="button" onClick={() => goTo(active - 1)} className={`${arrowClasses} left-0 sm:-left-2`} aria-label="Previous page" data-id="element-106" data-mp="2lrai580th09h">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-107" data-mp="2qkn0x1itecub" />
          </button>
          <button type="button" onClick={() => goTo(active + 1)} className={`${arrowClasses} right-0 sm:-right-2`} aria-label="Next page" data-id="element-108" data-mp="2vjzsfzkms838">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-109" data-mp="34i1umig3p6s3" />
          </button>
        </div>

        <div className="mt-6 flex justify-center gap-2" data-id="element-110" data-mp="39hwnusncm8gb">
          {previewPages.map((page, index) => <button key={page.src} type="button" onClick={() => goTo(index)} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-electric ${index === active ? 'w-6 bg-gold' : 'w-2 bg-electric/40 hover:bg-electric'}`} aria-label={`Go to page ${index + 1}`} aria-current={index === active} data-id="element-111" data-mp="1a529k9sybkdf" data-mpi={index} />)}
        </div>
      </div>
    </section>;
}